import { useState, useEffect } from "react";
import { Search, Filter, ArrowUpDown } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import BeatCard from "../components/BeatCard";
import LicenseModal from "../components/LicenseModal";
import FreeBeatModal from "../components/FreeBeatModal";
import { motion } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GENRES = ["All", "Hip-Hop", "Trap", "Emotional Trap", "R&B", "Pop", "Lo-Fi", "Drill", "Afrobeat", "EDM", "Soul", "Gospel"];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
];

export default function Store() {
  const [beats, setBeats] = useState([]);
  const [filteredBeats, setFilteredBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGenre, setSelectedGenre] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [selectedBeat, setSelectedBeat] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [freeModalOpen, setFreeModalOpen] = useState(false);

  useEffect(() => {
    const fetchBeats = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/beats`);
        setBeats(response.data);
        setFilteredBeats(response.data);
      } catch (error) {
        console.error("Error fetching beats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBeats();
  }, []);

  useEffect(() => {
    let filtered = beats;

    if (selectedGenre !== "All") {
      filtered = filtered.filter((beat) =>
        (beat.genres?.length ? beat.genres : [beat.genre]).includes(selectedGenre)
      );
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (beat) =>
          beat.title.toLowerCase().includes(query) ||
          (beat.genres?.length ? beat.genres : [beat.genre]).some((g) =>
            g.toLowerCase().includes(query)
          ) ||
          beat.key.toLowerCase().includes(query)
      );
    }

    const effectivePrice = (b) =>
      b.is_free ? 0 : b.price * (1 - (b.discount_percent || 0) / 100);
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === "price_asc") return effectivePrice(a) - effectivePrice(b);
      if (sortBy === "price_desc") return effectivePrice(b) - effectivePrice(a);
      return new Date(b.created_at) - new Date(a.created_at);
    });

    setFilteredBeats(filtered);
  }, [selectedGenre, searchQuery, sortBy, beats]);

  const handleBuy = (beat) => {
    setSelectedBeat(beat);
    if (beat.is_free) {
      setFreeModalOpen(true);
    } else {
      setModalOpen(true);
    }
  };

  return (
    <div className="pt-24 pb-12 px-6 lg:px-8" data-testid="store-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-heading text-4xl lg:text-5xl font-black">
            Beat Store
          </h1>
          <p className="text-muted-foreground mt-2 text-lg">
            Browse our collection of premium beats
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col lg:flex-row gap-4 mb-8"
        >
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search beats..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 rounded-xl bg-card border-border"
              data-testid="search-input"
            />
          </div>

          {/* Genre Filter - Desktop */}
          <div className="hidden lg:flex items-center gap-2 flex-wrap">
            {GENRES.map((genre) => (
              <Button
                key={genre}
                variant={selectedGenre === genre ? "default" : "outline"}
                onClick={() => setSelectedGenre(genre)}
                className={`rounded-full ${
                  selectedGenre === genre
                    ? "bg-primary hover:bg-primary/90"
                    : "border-border hover:bg-white/5"
                }`}
                data-testid={`genre-filter-${genre.toLowerCase()}`}
              >
                {genre}
              </Button>
            ))}
          </div>

          {/* Sort Control */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="rounded-xl gap-2 h-12 border-border hover:bg-white/5 lg:ml-auto"
                data-testid="sort-dropdown"
              >
                <ArrowUpDown className="w-4 h-4" />
                {SORT_OPTIONS.find((o) => o.value === sortBy)?.label}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 bg-popover border-border">
              {SORT_OPTIONS.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => setSortBy(option.value)}
                  className={`cursor-pointer ${sortBy === option.value ? "text-primary" : ""}`}
                  data-testid={`sort-option-${option.value}`}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Genre Filter - Mobile */}
          <div className="lg:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-xl gap-2 w-full justify-between" data-testid="genre-dropdown">
                  <span className="flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    {selectedGenre}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48 bg-popover border-border">
                {GENRES.map((genre) => (
                  <DropdownMenuItem
                    key={genre}
                    onClick={() => setSelectedGenre(genre)}
                    className="cursor-pointer"
                    data-testid={`mobile-genre-${genre.toLowerCase()}`}
                  >
                    {genre}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </motion.div>

        {/* Active Filter Badge */}
        {(selectedGenre !== "All" || searchQuery) && (
          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm text-muted-foreground">Filters:</span>
            {selectedGenre !== "All" && (
              <Badge
                variant="secondary"
                className="cursor-pointer hover:bg-destructive/20"
                onClick={() => setSelectedGenre("All")}
                data-testid="active-genre-filter"
              >
                {selectedGenre} ×
              </Badge>
            )}
            {searchQuery && (
              <Badge
                variant="secondary"
                className="cursor-pointer hover:bg-destructive/20"
                onClick={() => setSearchQuery("")}
                data-testid="active-search-filter"
              >
                "{searchQuery}" ×
              </Badge>
            )}
          </div>
        )}

        {/* Results Count */}
        <p className="text-sm text-muted-foreground mb-6">
          Showing {filteredBeats.length} beat{filteredBeats.length !== 1 ? "s" : ""}
        </p>

        {/* Beat List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-card rounded-2xl p-4 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-muted rounded-xl" />
                  <div className="flex-1">
                    <div className="h-6 bg-muted rounded w-48 mb-2" />
                    <div className="h-4 bg-muted rounded w-32" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredBeats.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <p className="text-muted-foreground text-lg">No beats found matching your criteria.</p>
            <Button
              variant="outline"
              className="mt-4 rounded-full"
              onClick={() => {
                setSelectedGenre("All");
                setSearchQuery("");
              }}
              data-testid="clear-filters-btn"
            >
              Clear Filters
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {filteredBeats.map((beat, index) => (
              <motion.div
                key={beat.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <BeatCard
                  beat={beat}
                  allBeats={filteredBeats}
                  onBuy={handleBuy}
                />
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* License Modal */}
      <LicenseModal
        beat={selectedBeat}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />

      {/* Free Beat Modal */}
      <FreeBeatModal
        beat={selectedBeat}
        open={freeModalOpen}
        onClose={() => setFreeModalOpen(false)}
      />
    </div>
  );
}
